/**
 * bc-manual
 *
 * Client side of the online product manual (sections/bc-manual-page.liquid).
 * The server always renders the complete all-products document — that is the
 * no-JS page and the PDF of the whole manual. This file adds two things:
 *
 *   1. PRODUCT FOCUS (shared manuals, N > 1 only). `?product=<handle>` or the
 *      cover picker (<select data-bc-manual-picker>) picks one product; the
 *      URL is kept in step with replaceState. With a focus set:
 *        - the root carries data-bc-manual-focus="<handle>";
 *        - every [data-bc-manual-product] (spec_matrix rows, ISA chart chips)
 *          gets .is-manual-focus when it matches, .is-manual-dim when not, and
 *          the matching <tr>s move to the top of their <tbody> in their own
 *          order (the original order is restored exactly when the focus
 *          clears). A product listed once per colourway has several rows
 *          (data-bc-manual-color tells them apart) and they move together:
 *          focus is by product;
 *        - [data-bc-manual-card] cover cards show only the focused product's
 *          (all hidden with no focus);
 *        - [data-bc-manual-applies="h1 h2 …"] conditional blocks hide when the
 *          focused product is not listed, and their "Applies to: …" line
 *          ([data-bc-manual-applies-list]) hides while a focus is set — the
 *          reader already knows which product they are looking at.
 *          A {products} token in the callout's sentence swaps its full list
 *          ([data-bc-manual-applies-inline]) for the focused product's name
 *          ([data-bc-manual-applies-name="<handle>"]) the same way.
 *      Only handles that are actually on the page are accepted (the picker's
 *      options, else the cards); anything else is ignored, never applied.
 *      N = 1 never focuses: there is nothing to pick between.
 *
 *   2. PRINT MODE. `?print=1` (what the PDF script loads) adds .bc-manual--print
 *      to the root — the print sheet applied on screen, so headless Chrome's
 *      --print-to-pdf and a person's print preview agree — loads every lazy
 *      image, opens every <details>, optionally replaces the stamp text with
 *      `?stamp=<text>` (as TEXT, never HTML), and when images and fonts have
 *      settled sets data-bc-manual-ready="1" on the root. The PDF script waits
 *      for that attribute.
 *
 * Every DOM write goes through one requestAnimationFrame (see
 * reference_horizon_style_recalc_flush_points: a full recalc on these pages
 * costs ~13-15ms, so writes are batched, never interleaved with reads).
 * Init is idempotent per root (data-bc-manual-init).
 */
(() => {
  // Upper bound on waiting for images/fonts before declaring print-ready. A
  // missing image must not hang the PDF build; it prints without it.
  const PRINT_READY_TIMEOUT_MS = 15000;

  const params = new URLSearchParams(window.location.search);

  /** Every lazy image in `root` → eager, and a promise that settles when all have loaded or failed. */
  function loadImages(root) {
    const imgs = Array.from(root.querySelectorAll('img'));
    imgs.forEach((img) => {
      if (img.loading === 'lazy') img.loading = 'eager';
    });
    return Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            }),
      ),
    );
  }

  function init(root) {
    if (root.dataset.bcManualInit) return;
    root.dataset.bcManualInit = '1';

    const count = parseInt(root.dataset.bcManualCount, 10) || 0;
    const multi = count > 1;
    const printMode = params.get('print') === '1';

    // A person printing from the browser menu: lazy images below the fold
    // would otherwise print as empty boxes in some engines.
    window.addEventListener('beforeprint', () => {
      root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    });

    const picker = multi ? root.querySelector('select[data-bc-manual-picker]') : null;
    const pickerWrap = multi ? root.querySelector('[data-bc-manual-picker-wrap]') : null;

    // The set of handles a focus may name: the picker's options, else the cards.
    const handles = new Set();
    if (picker) {
      Array.from(picker.options).forEach((o) => {
        if (o.value) handles.add(o.value);
      });
    } else {
      root.querySelectorAll('[data-bc-manual-card]').forEach((el) => {
        if (el.dataset.bcManualCard) handles.add(el.dataset.bcManualCard);
      });
    }
    const validate = (h) => (multi && h && handles.has(h) ? h : '');

    // tbody → its children in server order, snapshotted the first time a row
    // in it moves. Restoring re-appends that snapshot, which is exactly the
    // original order whatever moves happened in between.
    const originalRows = new Map();

    let focus = validate(params.get('product') || '');
    let rafId = 0;

    function write() {
      rafId = 0;

      if (focus) root.dataset.bcManualFocus = focus;
      else delete root.dataset.bcManualFocus;

      // Rows back to server order first, then the focused rows to the top.
      originalRows.forEach((rows, tbody) => tbody.append(...rows));

      root.querySelectorAll('[data-bc-manual-product]').forEach((el) => {
        const match = !!focus && el.dataset.bcManualProduct === focus;
        el.classList.toggle('is-manual-focus', match);
        el.classList.toggle('is-manual-dim', !!focus && !match);
      });

      if (focus) {
        const byBody = new Map();
        root.querySelectorAll('tr[data-bc-manual-product]').forEach((tr) => {
          if (tr.dataset.bcManualProduct !== focus) return;
          const tbody = tr.parentElement;
          if (!tbody || tbody.tagName !== 'TBODY') return;
          if (!byBody.has(tbody)) byBody.set(tbody, []);
          byBody.get(tbody).push(tr);
        });
        byBody.forEach((rows, tbody) => {
          if (!originalRows.has(tbody)) originalRows.set(tbody, Array.from(tbody.children));
          tbody.prepend(...rows);
        });
      }

      // Cards: N > 1 shows only the focused product's; N = 1 is left alone.
      if (multi) {
        root.querySelectorAll('[data-bc-manual-card]').forEach((card) => {
          card.hidden = card.dataset.bcManualCard !== focus;
        });
      }

      root.querySelectorAll('[data-bc-manual-applies]').forEach((el) => {
        const list = (el.dataset.bcManualApplies || '').split(/\s+/).filter(Boolean);
        el.hidden = !!focus && !list.includes(focus);
        el.querySelectorAll('[data-bc-manual-applies-list]').forEach((line) => {
          line.hidden = !!focus;
        });
        // A {products} token: the full inline list with no focus, only the
        // focused product's name with one (bc-manual-conditional).
        el.querySelectorAll('[data-bc-manual-applies-inline]').forEach((span) => {
          span.hidden = !!focus;
        });
        el.querySelectorAll('[data-bc-manual-applies-name]').forEach((span) => {
          span.hidden = !focus || span.dataset.bcManualAppliesName !== focus;
        });
      });

      if (picker && picker.value !== focus) picker.value = focus;
    }

    function schedule() {
      if (!rafId) rafId = requestAnimationFrame(write);
    }

    function setFocus(next) {
      next = validate(next);
      if (next === focus) return;
      focus = next;
      const url = new URL(window.location.href);
      if (focus) url.searchParams.set('product', focus);
      else url.searchParams.delete('product');
      history.replaceState(history.state, '', url);
      schedule();
    }

    if (picker) picker.addEventListener('change', () => setFocus(picker.value));

    // First paint of the script's state, in the same frame as the reveals.
    requestAnimationFrame(() => {
      printButtons.forEach((btn) => {
        btn.hidden = false;
      });
      if (pickerWrap) pickerWrap.hidden = false;
      if (printMode) root.classList.add('bc-manual--print');
    });
    if (multi) schedule();

    if (printMode) {
      root.querySelectorAll('details').forEach((d) => {
        d.open = true;
      });
      const stamp = params.get('stamp');
      if (stamp) {
        root.querySelectorAll('[data-bc-manual-stamp]').forEach((el) => {
          el.textContent = stamp;
        });
      }
      const settled = Promise.all([
        loadImages(root),
        document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve(),
      ]);
      const timeout = new Promise((resolve) => setTimeout(resolve, PRINT_READY_TIMEOUT_MS));
      Promise.race([settled, timeout]).then(() => {
        // Two frames: the first flushes any pending focus/print-class write,
        // the second runs after that layout has been committed.
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            root.dataset.bcManualReady = '1';
          }),
        );
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-bc-manual]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
